import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import apiClient from '../api/client';
import { canEditPage } from '../utils/rbac';
import { titleCase } from '../utils/format';
import { portfoliosApi, PortfolioListItem } from '../api/ppm';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';

const pretty = (v: any) => JSON.stringify(v ?? {}, null, 2);

const TemplatesPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'templates');

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [jsonText, setJsonText] = useState('{}');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'PROJECT',
    category: '',
    isActive: true,
    createdBy: '',
    includeSubprojects: false,
    includeNotes: false,
    includePermissions: false,
    includeMilestones: false,
    includeResources: false,
    includeTasks: false,
    includeResourcePlanning: false,
    includeFiles: false,
    includeStaffingRequests: false,
    templateDataJson: '',
  });

  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createProjectTemplateId, setCreateProjectTemplateId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [portfolioId, setPortfolioId] = useState('');
  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRow, setActionsRow] = useState<any | null>(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [topPanel, setTopPanel] = useState<null | 'total' | 'active' | 'withTasks' | 'withSubprojects'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, pRes] = await Promise.all([
        apiClient.get('/templates'),
        portfoliosApi.getAll(),
      ]);
      setRows(res.data || []);
      const ps = (pRes.data || []) as PortfolioListItem[];
      setPortfolios(ps);
      if (!portfolioId && ps.length) setPortfolioId(ps[0].id);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    refresh();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const cols: GridColDef[] = useMemo(() => {
    const base: GridColDef[] = [
      { field: 'id', headerName: 'ID', width: 220 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 260 },
      { field: 'type', headerName: 'Type', width: 140, valueGetter: (p) => (p.row?.type ? titleCase(String(p.row.type)) : '—') },
      { field: 'category', headerName: 'Category', width: 180 },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 120,
        sortable: true,
        renderCell: (p) =>
          p.row?.isActive ? (
            <Chip size="small" label="Active" color="success" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
          ) : (
            <Chip size="small" label="Inactive" color="default" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
          ),
      },
      { field: 'createdAt', headerName: 'Created', width: 180, valueGetter: (p) => (p.row?.createdAt ? String(p.row.createdAt).slice(0, 19).replace('T', ' ') : '—') },
    ];

    if (!canEdit) return base;
    return [
      ...base,
      {
        field: 'actions',
        headerName: 'Admin',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setActionsAnchorEl(e.currentTarget);
              setActionsRow(params.row);
            }}
            aria-label="Actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [canEdit]);

  const summary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => !!r.isActive).length;
    const withTasks = rows.filter((r) => !!r.includeTasks).length;
    const withSubprojects = rows.filter((r) => !!r.includeSubprojects).length;
    return { total, active, withTasks, withSubprojects };
  }, [rows]);

  const activeMix = useMemo(() => {
    const active = summary.active;
    const inactive = Math.max(0, summary.total - summary.active);
    return [
      { name: 'Active', value: active },
      { name: 'Inactive', value: inactive },
    ];
  }, [summary.total, summary.active]);

  const featureMix = useMemo(() => {
    const features = [
      { name: 'Tasks', value: summary.withTasks },
      { name: 'Subprojects', value: summary.withSubprojects },
      { name: 'Milestones', value: rows.filter((r: any) => !!r.includeMilestones).length },
      { name: 'Resources', value: rows.filter((r: any) => !!r.includeResources).length },
    ];
    return features;
  }, [rows, summary.withTasks, summary.withSubprojects]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      type: 'PROJECT',
      category: '',
      isActive: true,
      createdBy: '',
      includeSubprojects: false,
      includeNotes: false,
      includePermissions: false,
      includeMilestones: false,
      includeResources: false,
      includeTasks: false,
      includeResourcePlanning: false,
      includeFiles: false,
      includeStaffingRequests: false,
      templateDataJson: '',
    });
    setJsonText(pretty({}));
    setShowAdvanced(false);
    setEditOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      type: row.type || 'PROJECT',
      category: row.category || '',
      isActive: row.isActive !== undefined ? !!row.isActive : true,
      createdBy: row.createdBy || '',
      includeSubprojects: !!row.includeSubprojects,
      includeNotes: !!row.includeNotes,
      includePermissions: !!row.includePermissions,
      includeMilestones: !!row.includeMilestones,
      includeResources: !!row.includeResources,
      includeTasks: !!row.includeTasks,
      includeResourcePlanning: !!row.includeResourcePlanning,
      includeFiles: !!row.includeFiles,
      includeStaffingRequests: !!row.includeStaffingRequests,
      templateDataJson: row.templateData ? pretty(row.templateData) : '',
    });
    setJsonText(pretty(row));
    setShowAdvanced(false);
    setEditOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim()) {
        setToast({ severity: 'error', message: 'Name is required' });
        setToastOpen(true);
        return;
      }

      let templateData: any | undefined = undefined;
      if (form.templateDataJson.trim()) {
        try {
          templateData = JSON.parse(form.templateDataJson);
        } catch (e: any) {
          setToast({ severity: 'error', message: 'Template Data must be valid JSON (Advanced section)' });
          setToastOpen(true);
          return;
        }
      }

      const payload: any = {
        name: form.name.trim(),
        description: form.description.trim() ? form.description.trim() : undefined,
        type: form.type,
        category: form.category.trim() ? form.category.trim() : undefined,
        isActive: !!form.isActive,
        createdBy: form.createdBy.trim() ? form.createdBy.trim() : undefined,
        includeSubprojects: !!form.includeSubprojects,
        includeNotes: !!form.includeNotes,
        includePermissions: !!form.includePermissions,
        includeMilestones: !!form.includeMilestones,
        includeResources: !!form.includeResources,
        includeTasks: !!form.includeTasks,
        includeResourcePlanning: !!form.includeResourcePlanning,
        includeFiles: !!form.includeFiles,
        includeStaffingRequests: !!form.includeStaffingRequests,
        templateData,
      };

      if (showAdvanced) {
        // optional advanced override if someone edits whole JSON
        try {
          const advanced = JSON.parse(jsonText || '{}');
          Object.assign(payload, advanced);
        } catch {
          // ignore invalid JSON and just use form payload
        }
      }
      if (editing?.id) {
        await apiClient.put(`/templates/${editing.id}`, payload);
        setToast({ severity: 'success', message: 'Updated' });
      } else {
        await apiClient.post('/templates', payload);
        setToast({ severity: 'success', message: 'Created' });
      }
      setToastOpen(true);
      setEditOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.message || 'Save failed (invalid JSON?)' });
      setToastOpen(true);
    }
  };

  const createProject = async () => {
    if (!createProjectTemplateId) return;
    if (!portfolioId) {
      setToast({ severity: 'error', message: 'Select a portfolio' });
      setToastOpen(true);
      return;
    }
    try {
      await apiClient.post(`/templates/${createProjectTemplateId}/create-project`, {
        name: projectName || 'New Project',
        portfolioId,
      });
      setCreateProjectOpen(false);
      setToast({ severity: 'success', message: 'Project created from template' });
      setToastOpen(true);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Create project failed' });
      setToastOpen(true);
    }
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Templates"
        subtitle="Manage templates and create projects from them."
        actions={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            {canEdit ? (
              <Button variant="contained" onClick={openCreate} sx={{ borderRadius: 2 }}>
                Add
              </Button>
            ) : null}
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total"
            value={summary.total}
            subtitle="Templates"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'total' ? null : 'total'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Active"
            value={summary.active}
            subtitle="Usable now"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'active' ? null : 'active'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Includes Tasks"
            value={summary.withTasks}
            subtitle="Template feature"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'withTasks' ? null : 'withTasks'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Includes Subprojects"
            value={summary.withSubprojects}
            subtitle="Template feature"
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'withSubprojects' ? null : 'withSubprojects'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'total' ? 'Templates' : topPanel === 'active' ? 'Active templates' : topPanel === 'withTasks' ? 'Templates with tasks' : 'Templates with subprojects'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const list =
                  topPanel === 'total'
                    ? rows
                    : topPanel === 'active'
                      ? rows.filter((r) => !!r.isActive)
                      : topPanel === 'withTasks'
                        ? rows.filter((r) => !!r.includeTasks)
                        : rows.filter((r) => !!r.includeSubprojects);

                if (!list.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                return (
                  <Stack spacing={1}>
                    {list.slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                      <Box
                        key={r.id}
                        sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(r);
                          setDetailsOpen(true);
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={String(r.name || '')}>
                            {String(r.name || 'Untitled template')}
                          </Typography>
                          <Chip size="small" label={r.isActive ? 'Active' : 'Inactive'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.type ? titleCase(String(r.type)) : '—'} • {r.category || '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Active vs inactive" subtitle="Template availability" height={260}>
            <NexusDonut
              height={200}
              centerLabel="Total"
              centerValue={String(summary.total)}
              data={[
                { name: 'Active', value: activeMix[0].value, color: tokens.color.success },
                { name: 'Inactive', value: activeMix[1].value, color: tokens.color.neutralSoft },
              ]}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Feature mix" subtitle="How templates are configured" height={260}>
            <NexusBarChart
              data={featureMix}
              xKey="name"
              height={220}
              series={[{ key: 'value', name: 'Templates' }]}
              valueFormatter={(v) => String(Math.round(v))}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Top template types" subtitle="Most common types" height={260}>
            <NexusBarChart
              data={Array.from(
                (rows || []).reduce((m: Map<string, number>, r: any) => {
                  const t = String(r.type || 'UNKNOWN').toUpperCase();
                  m.set(t, (m.get(t) || 0) + 1);
                  return m;
                }, new Map<string, number>()),
              )
                .map(([name, value]) => ({ name, value }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 6)}
              xKey="name"
              height={220}
              series={[{ key: 'value', name: 'Templates' }]}
              valueFormatter={(v) => String(Math.round(v))}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <TableCard title="Template list" subtitle="Click a row for details. Admin actions are in the overflow menu." actions={loading ? <CircularProgress size={18} /> : null}>
        <div style={{ height: 600, width: '100%' }}>
          <ResizableDataGrid
            storageKey="gridWidths.templates"
            enableDensityControls
            rows={rows}
            columns={cols}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            onRowClick={(p) => {
              setActionsRow(p.row);
              setDetailsOpen(true);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
              '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
            }}
          />
        </div>
      </TableCard>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField
              label="Type"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              select
              fullWidth
            >
              {['PROJECT', 'PMO', 'ORGANIZATIONAL', 'TRAINING', 'PSA'].map((t) => (
                <MenuItem key={t} value={t}>
                  {titleCase(t)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} fullWidth />
            <TextField label="Created By (optional)" value={form.createdBy} onChange={(e) => setForm((f) => ({ ...f, createdBy: e.target.value }))} fullWidth />
          </Box>

          <TextField
            sx={{ mt: 1.25 }}
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />

          <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.5 }}>
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
            <FormControlLabel control={<Switch checked={form.includeSubprojects} onChange={(e) => setForm((f) => ({ ...f, includeSubprojects: e.target.checked }))} />} label="Include Subprojects" />
            <FormControlLabel control={<Switch checked={form.includeMilestones} onChange={(e) => setForm((f) => ({ ...f, includeMilestones: e.target.checked }))} />} label="Include Milestones" />
            <FormControlLabel control={<Switch checked={form.includeTasks} onChange={(e) => setForm((f) => ({ ...f, includeTasks: e.target.checked }))} />} label="Include Tasks" />
            <FormControlLabel control={<Switch checked={form.includeResources} onChange={(e) => setForm((f) => ({ ...f, includeResources: e.target.checked }))} />} label="Include Resources" />
            <FormControlLabel control={<Switch checked={form.includeResourcePlanning} onChange={(e) => setForm((f) => ({ ...f, includeResourcePlanning: e.target.checked }))} />} label="Include Resource Planning" />
            <FormControlLabel control={<Switch checked={form.includeNotes} onChange={(e) => setForm((f) => ({ ...f, includeNotes: e.target.checked }))} />} label="Include Notes" />
            <FormControlLabel control={<Switch checked={form.includePermissions} onChange={(e) => setForm((f) => ({ ...f, includePermissions: e.target.checked }))} />} label="Include Permissions" />
            <FormControlLabel control={<Switch checked={form.includeFiles} onChange={(e) => setForm((f) => ({ ...f, includeFiles: e.target.checked }))} />} label="Include Files" />
            <FormControlLabel control={<Switch checked={form.includeStaffingRequests} onChange={(e) => setForm((f) => ({ ...f, includeStaffingRequests: e.target.checked }))} />} label="Include Staffing Requests" />
          </Box>

          <Accordion sx={{ mt: 1.25 }} expanded={showAdvanced} onChange={(_, v) => setShowAdvanced(v)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 650 }}>Advanced (JSON)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary">
                Template Data is optional. If you paste JSON here, it will be saved to `templateData`.
              </Typography>
              <TextField
                sx={{ mt: 1 }}
                label="Template Data (JSON)"
                value={form.templateDataJson}
                onChange={(e) => setForm((f) => ({ ...f, templateDataJson: e.target.value }))}
                fullWidth
                multiline
                minRows={8}
              />
              <TextField
                sx={{ mt: 1 }}
                label="Full Payload Override (JSON)"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                fullWidth
                multiline
                minRows={8}
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createProjectOpen} onClose={() => setCreateProjectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create project from template</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Project Name" value={projectName} onChange={(e) => setProjectName(e.target.value)} fullWidth />
          <TextField select label="Portfolio" value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)} fullWidth>
            <MenuItem value="">
              <em>Select portfolio</em>
            </MenuItem>
            {portfolios.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCreateProjectOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createProject}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={() => {
          setActionsAnchorEl(null);
          setActionsRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionsRow) openEdit(actionsRow);
            setActionsAnchorEl(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionsRow) return;
            setCreateProjectTemplateId(actionsRow.id);
            setProjectName('');
            setPortfolioId('demo_portfolio_core');
            setCreateProjectOpen(true);
            setActionsAnchorEl(null);
          }}
        >
          Create Project…
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!actionsRow) return;
            // eslint-disable-next-line no-alert
            const ok = window.confirm(`Delete template "${actionsRow.name}"?`);
            if (!ok) return;
            try {
              await apiClient.delete(`/templates/${actionsRow.id}`);
              setToast({ severity: 'success', message: 'Template deleted' });
              setToastOpen(true);
              refresh();
            } catch (e: any) {
              setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
              setToastOpen(true);
            } finally {
              setActionsAnchorEl(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Template</DialogTitle>
        <DialogContent>
          {actionsRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={actionsRow.isActive ? 'Active' : 'Inactive'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={actionsRow.type ? titleCase(String(actionsRow.type)) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={actionsRow.category || '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 650 }}>
                {actionsRow.name}
              </Typography>
              {actionsRow.description ? <Typography variant="body2">{actionsRow.description}</Typography> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {canEdit && actionsRow ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(actionsRow)}>
                Edit
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCreateProjectTemplateId(actionsRow.id);
                  setProjectName('');
                  setPortfolioId('demo_portfolio_core');
                  setCreateProjectOpen(true);
                }}
              >
                Create Project…
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default TemplatesPage;


